import {
  HHBox,
  HHCircularProgress,
  HHGrid,
  HHLabelSection,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { HCModal } from '../../../components/hinge-connect-modal';
import {
  healthRecordsDetailLabels,
  healthRecordsInfoMessages,
} from '../../constants/health-records.constant';
import { useGetHealthRecordQuery } from '../../types';

const HealthRecordMetadataItem = ({
  label,
  content,
}: {
  label: string;
  content: string;
}): JSX.Element => (
  <HHGrid item xs={12} sm={6}>
    <HHLabelSection hhVariant="text" {...{ label, content }} />
  </HHGrid>
);

const HealthRecordDetails = ({ id }: { id: string }): JSX.Element => {
  const { data, loading, error } = useGetHealthRecordQuery({
    variables: { id },
  });

  if (loading)
    return (
      <HHBox display="flex" alignItems="center" justifyContent="center">
        <HHCircularProgress />
      </HHBox>
    );
  if (error)
    return (
      <HHStack spacing={4}>
        <HHTypography hhVariant="h2">
          {healthRecordsInfoMessages.oops}
        </HHTypography>
        <HHTypography hhVariant="body">{error.message}</HHTypography>
      </HHStack>
    );
  if (!data)
    return (
      <HHBox display="flex" alignItems="center" justifyContent="center">
        {healthRecordsInfoMessages.recordNotFound}
      </HHBox>
    );
  return (
    <HHGrid container>
      <HealthRecordMetadataItem
        label={healthRecordsDetailLabels.id}
        content={data.getHealthRecord.id}
      />
      <HealthRecordMetadataItem
        label={healthRecordsDetailLabels.source}
        content={data.getHealthRecord.source}
      />
      <HealthRecordMetadataItem
        label={healthRecordsDetailLabels.resourceType}
        content={data.getHealthRecord.resourceType}
      />
      <HealthRecordMetadataItem
        label={healthRecordsDetailLabels.origin}
        content={data.getHealthRecord.origin ?? healthRecordsDetailLabels.na}
      />
      <HealthRecordMetadataItem
        label={healthRecordsDetailLabels.resourceId}
        content={data.getHealthRecord.resourceId}
      />
      <HealthRecordMetadataItem
        label={healthRecordsDetailLabels.userId}
        content={data.getHealthRecord.userId}
      />
      <HealthRecordMetadataItem
        label={healthRecordsDetailLabels.createdAt}
        content={data.getHealthRecord.createdAt}
      />
      <HHGrid item xs={12}>
        <HHTypography hhVariant="h6">
          {healthRecordsDetailLabels.data}
        </HHTypography>
        <pre data-testid="health-record-data">
          {JSON.stringify(data.getHealthRecord.data, null, 2)}
        </pre>
      </HHGrid>
    </HHGrid>
  );
};

type HealthRecordDetailModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  id: string;
};

export const HealthRecordDetailModal = ({
  isOpen,
  handleClose,
  id,
}: HealthRecordDetailModalProps): JSX.Element => (
  <HCModal
    title={healthRecordsDetailLabels.title}
    isOpen={isOpen}
    handleClose={handleClose}
    component={<HealthRecordDetails id={id} />}
  />
);
